/* @flow */ /* eslint no-undef: 0 */
import React from 'react';
import classNames from 'classnames';
import { SVGs } from '../../common/fa_icons';
import {every, omitBy, isNull, isNumber} from 'lodash';
import { windowAttrs } from '../../../utils/dom';
import { BREAKPOINT_MEDIUM_PX } from '../../../constants';
import { decorateIntlMessages } from '../../common/rosetta_formatted_message';
import { defineMessages } from 'react-intl';

/*
 * This file contains HOCs intended for use by carousel and slide components
 * that make use of the react-slick API.
 *
 * See: https://github.com/akiran/react-slick
 */

const SHOPPING_RIGHT_ARROW_PADDING = 60;
const SHOPPING_RIGHT_ARROW_RESPONSIVE_PADDING = 25;

const i18nMessages = decorateIntlMessages(__filename, defineMessages({
  previous_button_title: {
    id: 'previous_button_title',
    description: 'Previous button title attribute content (Previous)',
    defaultMessage: 'Previous'
  },
  next_button_title: {
    id: 'next_button_title',
    description: 'Next button title attribute content (Next)',
    defaultMessage: 'Next'
  }
}));

type ReactSliderProps = {
  onClick: Function,
  className: string
};
type Options = {
  widths: Object,
  shouldHide: bool
};
type UUID =
  string|number;

type Intl = {
  formatMessage: Function
};

const getShimmedRightAlignment =
  (carouselType: string|null, { slideWidth, carouselWidth }: Object): number|null => {
    if (carouselType === 'product') {
      carouselWidth = windowAttrs().innerWidth >= BREAKPOINT_MEDIUM_PX ? carouselWidth
        : windowAttrs().innerWidth - SHOPPING_RIGHT_ARROW_RESPONSIVE_PADDING;
    }

    return every([slideWidth, carouselWidth], isNumber)
      ? (carouselType === 'product' ? (slideWidth - carouselWidth + SHOPPING_RIGHT_ARROW_PADDING) : slideWidth - carouselWidth)
      : null;
  };

const getShimmedDisplayState =
  (shouldHide: bool): string|null =>
    shouldHide
      ? 'none'
      : null;

const getShimmedNextArrowStyle =
  (carouselType: string|null, { widths = {}, shouldHide = false }: Options|Object = {}): Object =>
    omitBy({
      right: getShimmedRightAlignment(carouselType, widths),
      display: getShimmedDisplayState(shouldHide)
    }, isNull);

/*
 * PrevArrow and NextArrow onClick and className properties are implicitly
 * passed by react-slick.
 *
 * Documentation: http://bit.ly/2eeh4xn
 */

export const getPrevArrow =
  (uuid: UUID, arrowType: string, intl: Intl): Function =>
    ({ onClick, className }: ReactSliderProps): React$Element<any> => (
      <button key={uuid}
        onClick={onClick}
        id={`prev-arrow-${uuid}`}
        className={classNames('fa fa-' + arrowType, className)}
        dangerouslySetInnerHTML={{ __html: SVGs[arrowType] }}
        title={intl.formatMessage(i18nMessages.previous_button_title)}
      />
    );

export const getNextArrow =
  (uuid: UUID, carouselType: string|null, options: Options, arrowType: string, intl: Intl): Function =>
    ({ onClick, className }: ReactSliderProps): React$Element<any> =>
      <button key={uuid}
        onClick={onClick}
        id={`next-arrow-${uuid}`}
        style={getShimmedNextArrowStyle(carouselType, options)}
        className={classNames('fa fa-' + arrowType, className)}
        dangerouslySetInnerHTML={{ __html: SVGs[arrowType] }}
        title={intl.formatMessage(i18nMessages.next_button_title)}
      />;
